import { tr } from '@/lib/i18n-core';
import { useMutation } from '@tanstack/react-query';
import { Printer } from 'lucide-react';
import { get, qs } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { printDocument } from '@/lib/print';
import { Button } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import type { QueryParams } from './paging-export';

interface PrintedDocument {
  html: string;
  filename: string;
}

interface PrintRequest {
  path: string;
  params?: QueryParams;
}

export function usePrintServerDocument() {
  const toast = useToast();
  return useMutation({
    mutationFn: (request: PrintRequest) => get<PrintedDocument>(`${request.path}${qs({ ...request.params, format: 'html' })}`),
    onSuccess: (printed) => printDocument(printed.html),
    onError: (error) => toast.error(tr('pharmacy', 'could_not_print'), describeError(error)),
  });
}

export function PrintDocumentButton({
  path,
  params,
  label,
  compact,
  disabled,
  size = 'sm',
}: {
  path: string;
  params?: QueryParams;
  label: string;
  compact?: boolean;
  disabled?: boolean;
  size?: 'sm' | 'default';
}) {
  const print = usePrintServerDocument();

  return (
    <Button
      type="button"
      size={compact ? 'icon' : size}
      variant={compact ? 'ghost' : 'outline'}
      aria-label={label}
      title={label}
      disabled={disabled}
      loading={print.isPending}
      onClick={(event) => {
        event.stopPropagation();
        print.mutate({ path, params });
      }}
    >
      {!(compact && print.isPending) && <Printer className="h-4 w-4" />}
      {compact ? null : label}
    </Button>
  );
}
